// Tiendas — Portal Admin VentasLuga. Banda de periodo y explorer filtros · tabla · ficha.
// La tienda va en `?tienda=` y el periodo en `?periodo=`.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import {
    DEFAULT_REPORT_PERIOD,
    PeriodBand,
    parseReportPeriod,
    type ReportPeriod,
} from '@/shared/patterns/PeriodBand';
import { ReconciliationEntityEnum } from '@fiado/type-kit/bin/retailIntegration/enums/ReconciliationEntityEnum';
import { usePermissions } from '@/kit';
import { useAuth } from '@/hooks/useAuth';
import { resolveOwnRetailerId } from '@/domains/retail/ventas/lib/retailerScope';
import { useRosterRetailer, useRosterUsers } from '../lib/useRoster';
import { useLegacySaleCategories, useStoreSellers } from './api/useTiendas';
import {
    useStoreDetail,
    useStorePeriodSales,
    useStoresTable,
    useStoresTrend,
} from './api/useStoresTable';
import { StoresFiltersPanel } from './components/StoresFiltersPanel';
import { NewStoreButton } from './components/NewStoreDrawer';
import { ReconcileButton } from '../reconciliation/components/ReconcileButton';
import { RetailerIntegrationLock } from '../integration/components/IntegrationLock';
import { useRetailerIntegrated } from '../integration/api/useRetailerIntegration';
import { StoresTable, StoreStatusTabs } from './components/StoresTable';
import { StoreDetailPanel, type StoreDetailTab } from './components/StoreDetailPanel';
import type { SalesBlock } from './components/StoreSalesTab';
import { categoryMixFromDetail, legacySaleSkus } from './lib/categoryMix';
import { buildTeamRows, TREND_WEEKS } from './lib/storeDetail';
import {
    buildStoreContext,
    DEFAULT_STORE_SORT,
    DEFAULT_STORE_STATUS_TAB,
    EMPTY_STORES_FILTERS,
    filterStoreRows,
    filterStoresByStatus,
    storeStatusCounts,
    sortStoreRows,
    toggleStoreSort,
    type StoreSort,
    type StoresFilters,
    type StoreStatusTab,
} from './lib/storesTable';

/** El permiso del listado de ventas, que no es el de esta pantalla. */
const SALES_PERMISSION = 'retail.sale.read.all';

/** La categoría de las ventas sin líneas sale de la lente del catálogo. */
const LENS_PERMISSION = 'retail.inventory.lens.read';

const NO_LEGACY_CATEGORIES = new Map();

const RECONCILE_TARGET = { entity: ReconciliationEntityEnum.STORES } as const;

/** Reparto lista / ficha a 1440: la tabla cabe sin empujar la ficha fuera de la pantalla. */
const SPLIT = { list: 'minmax(0,3fr)', detail: 'minmax(400px,2fr)' };

function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}

/** Con la cadena integrada, las altas quedan bajo candado. */
export function TiendasPage() {
    const { retailerId } = useRosterRetailer();
    return (
        <RetailerIntegrationLock retailerId={retailerId}>
            <TiendasScreen />
        </RetailerIntegrationLock>
    );
}

function TiendasScreen() {
    const navigate = useNavigate();
    const { tienda, periodo } = useSearch({ from: '/_backoffice/retail-admin/$section' });
    const period = parseReportPeriod(periodo);

    const [filters, setFilters] = useState<StoresFilters>(EMPTY_STORES_FILTERS);
    const [sort, setSort] = useState<StoreSort>(DEFAULT_STORE_SORT);
    const [statusTab, setStatusTab] = useState<StoreStatusTab>(DEFAULT_STORE_STATUS_TAB);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [detailTab, setDetailTab] = useState<StoreDetailTab>('performance');

    const { roleAssignments } = useAuth();
    const fixedRetailerId = resolveOwnRetailerId(roleAssignments);
    const { hasPermission } = usePermissions();
    const salesBlock: SalesBlock = !hasPermission(SALES_PERMISSION)
        ? 'noPermission'
        : fixedRetailerId === null
          ? 'noRetailer'
          : 'ok';

    const list = useStoresTable(period);
    const detail = useStoreDetail(tienda ?? null, period);
    // Sin la ficha del BFF, la serie y el mix salen del respaldo (8 lecturas + ventas de la tienda).
    const onPerformance = Boolean(tienda) && detailTab === 'performance';
    const detailFallback = onPerformance && (detail.missing || detail.isError);
    const trend = useStoresTrend(detailFallback);
    const onTeam = detailTab === 'team';
    const sellers = useStoreSellers(period, onTeam);
    const { retailerId } = useRosterRetailer();
    const integrated = useRetailerIntegrated(retailerId);
    const users = useRosterUsers(onTeam ? retailerId : null);
    const needsSales = detailTab === 'sales' || detailFallback;
    const sales = useStorePeriodSales(
        needsSales ? (tienda ?? null) : null,
        salesBlock === 'ok' ? fixedRetailerId : null,
        period,
    );
    const detailMix = useMemo(
        () => (detail.data ? categoryMixFromDetail(detail.data.categoryMix) : undefined),
        [detail.data],
    );
    const legacySkus = useMemo(() => legacySaleSkus(sales.data?.items ?? []), [sales.data]);
    const legacyCategories = useLegacySaleCategories(legacySkus, hasPermission(LENS_PERMISSION));

    const rows = useMemo(() => list.data?.items ?? [], [list.data]);
    const context = useMemo(() => buildStoreContext(rows), [rows]);
    const statusCounts = useMemo(() => storeStatusCounts(rows), [rows]);
    const statusRows = useMemo(() => filterStoresByStatus(rows, statusTab), [rows, statusTab]);
    const visibleRows = useMemo(
        () => sortStoreRows(filterStoreRows(statusRows, filters, context), sort, context),
        [statusRows, filters, context, sort],
    );
    const selectedRow = rows.find((r) => r.storeId === tienda);
    const listError = asError(list.error);

    const teamMembers = useMemo(() => {
        if (!tienda || !users.data || !sellers.byStore) return undefined;
        return buildTeamRows(
            tienda,
            selectedRow?.managerId ?? null,
            users.data,
            sellers.byStore.get(tienda) ?? [],
        );
    }, [tienda, users.data, sellers.byStore, selectedRow?.managerId]);

    const selectStore = (storeId: string | null) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'tiendas' },
            search: (prev) => ({ ...prev, tienda: storeId ?? undefined }),
            replace: true,
        });
    };

    const changePeriod = (next: ReportPeriod) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'tiendas' },
            search: (prev) => ({
                ...prev,
                periodo: next === DEFAULT_REPORT_PERIOD ? undefined : next,
            }),
            replace: true,
        });
    };

    const openSeller = (sellerId: string) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'vendedores' },
            search: { vendedor: sellerId },
        });
    };

    // Autoselecciona la primera fila solo si no hay selección; un `?tienda=` fuera de la lista se respeta.
    useEffect(() => {
        if (!tienda && visibleRows.length > 0) {
            selectStore(visibleRows[0].storeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectStore se re-crea en cada render
    }, [tienda, visibleRows]);

    return (
        <ScreenFrame>
            <PeriodBand
                period={period}
                onPeriodChange={changePeriod}
                truncated={list.data?.truncated ?? false}
                goalNote={rows.some((r) => r.goalCents !== null)}
            />
            <MasterDetailExplorer
                filtersCollapsed={filtersCollapsed}
                split={SPLIT}
                filters={
                    <StoresFiltersPanel
                        filters={filters}
                        onChange={setFilters}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((v) => !v);
                        }}
                    />
                }
                list={
                    <StoresTable
                        rows={visibleRows}
                        allRows={statusRows}
                        hasAnyStore={rows.length > 0}
                        statusTabs={
                            <StoreStatusTabs
                                active={statusTab}
                                counts={list.data ? statusCounts : null}
                                onChange={setStatusTab}
                            />
                        }
                        context={context}
                        sort={sort}
                        onSortToggle={(key) => {
                            setSort((current) => toggleStoreSort(current, key));
                        }}
                        pill={filters.pill}
                        onPillChange={(pill) => {
                            setFilters({ ...filters, pill });
                        }}
                        selectedId={tienda ?? null}
                        onSelect={selectStore}
                        isLoading={list.isPending}
                        error={listError}
                        onRetry={list.refetch}
                        period={period}
                        teamAvailable={list.data?.teamAvailable ?? false}
                        actions={
                            <>
                                <ReconcileButton
                                    retailerId={retailerId}
                                    target={RECONCILE_TARGET}
                                    onComplete={list.refetch}
                                />
                                <NewStoreButton retailerId={retailerId} />
                            </>
                        }
                    />
                }
                detail={
                    <StoreDetailPanel
                        row={selectedRow}
                        rows={rows}
                        integrated={integrated}
                        context={context}
                        hasSelection={Boolean(tienda)}
                        isLoading={list.isPending}
                        error={listError}
                        onRetry={list.refetch}
                        tab={detailTab}
                        onTabChange={setDetailTab}
                        team={{
                            members: teamMembers,
                            isError: users.isError || sellers.isError,
                        }}
                        sales={{
                            block: salesBlock,
                            items: sales.data?.items,
                            truncated: sales.data?.truncated ?? false,
                            isLoading: sales.isPending && salesBlock === 'ok',
                            error: asError(sales.error),
                            legacyCategories: legacyCategories ?? NO_LEGACY_CATEGORIES,
                        }}
                        trend={
                            detail.data
                                ? {
                                      series: detail.data.weeklyPlacement.map(
                                          (week) => week.placedCents,
                                      ),
                                      isError: false,
                                  }
                                : {
                                      series: tienda
                                          ? (trend.byStore?.get(tienda) ?? emptySeries(trend))
                                          : undefined,
                                      isError: trend.isError,
                                  }
                        }
                        categoryMix={detailMix}
                        period={period}
                        onOpenSeller={openSeller}
                    />
                }
            />
        </ScreenFrame>
    );
}

/** La tienda que no vendió en ninguna de las 8 semanas tiene una serie en ceros, no vacía. */
function emptySeries(trend: { byStore: Map<string, number[]> | undefined }): number[] | undefined {
    return trend.byStore === undefined ? undefined : new Array<number>(TREND_WEEKS).fill(0);
}
